(() => {
  if (customElements.get('bf-product-media-carousel')) return;

  class BFProductMediaCarousel extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.track = this.querySelector('[data-bf-ugc-track]');
      this.cards = Array.from(this.querySelectorAll('[data-bf-ugc-card]'));
      this.previousButton = this.querySelector('[data-bf-ugc-previous]');
      this.nextButton = this.querySelector('[data-bf-ugc-next]');
      this.currentElement = this.querySelector('[data-bf-ugc-current]');
      this.totalElement = this.querySelector('[data-bf-ugc-total]');
      this.abortController = new AbortController();
      this.scrollFrame = null;
      this.scrollEndTimer = null;
      this.programmaticScrollUntil = 0;
      this.activeIndex = 0;
      this.currentPage = 1;
      this.totalPages = 1;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

      if (!this.track || this.cards.length === 0) return;

      const signal = this.abortController.signal;
      this.previousButton?.addEventListener('click', () => this.move(-1), {signal});
      this.nextButton?.addEventListener('click', () => this.move(1), {signal});
      this.track.addEventListener('scroll', () => this.handleScroll(), {passive: true, signal});
      this.track.addEventListener('keydown', (event) => this.handleKeydown(event), {signal});
      this.addEventListener('click', (event) => this.handleControl(event), {signal});
      window.addEventListener('bf:media-play', (event) => {
        if (event.detail?.owner !== this) this.pauseAll();
      }, {signal});
      document.addEventListener('shopify:block:select', (event) => this.handleBlockSelect(event), {signal});
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseAll();
      }, {signal});

      this.cards.forEach((card) => {
        const video = card.querySelector('[data-bf-ugc-video]');
        const iframe = card.querySelector('[data-bf-ugc-iframe]');
        video?.addEventListener('play', () => this.markPlaying(card, true), {signal});
        video?.addEventListener('pause', () => this.markPlaying(card, false), {signal});
        video?.addEventListener('ended', () => this.pauseCard(card), {signal});
        iframe?.addEventListener('load', () => this.applyIframeSound(card), {signal});

      });

      this.resizeObserver = new ResizeObserver(() => this.recalculate());
      this.resizeObserver.observe(this.track);

      this.viewportObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry?.isIntersecting) {
            this.pauseAll();
          } else if (!this.reducedMotion.matches) {
            this.playCard(this.cards[this.activeIndex] || this.cards[0], 'automatic');
          }
        },
        {threshold: 0.1}
      );
      this.viewportObserver.observe(this);
      this.recalculate();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.viewportObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
      this.pauseAll();
      this.initialized = false;
    }

    recalculate() {
      if (!this.track || this.cards.length === 0) return;

      const firstCard = this.cards[0];
      const secondCard = this.cards[1];
      this.itemOffset = secondCard ? secondCard.offsetLeft - firstCard.offsetLeft : firstCard.clientWidth;
      this.totalPages = this.cards.length;
      this.update();
    }

    handleScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.update();
      });
      if (this.scrollEndTimer) clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = setTimeout(() => this.activateNearestCard(), 140);
    }

    update() {
      this.currentPage = this.activeIndex + 1;

      if (this.currentElement) this.currentElement.textContent = String(this.currentPage);
      if (this.totalElement) this.totalElement.textContent = String(this.cards.length);

      if (this.previousButton) this.previousButton.disabled = this.activeIndex <= 0;
      if (this.nextButton) this.nextButton.disabled = this.activeIndex >= this.cards.length - 1;

      const playingCard = this.cards.find((card) => card.classList.contains('is-playing'));
      if (playingCard && !this.isMostlyVisible(playingCard)) this.pauseCard(playingCard);

      this.cards.forEach((card, index) => {
        if (index === this.activeIndex) card.setAttribute('aria-current', 'true');
        else card.removeAttribute('aria-current');
      });
    }

    move(direction) {
      const targetIndex = Math.min(this.cards.length - 1, Math.max(0, this.activeIndex + direction));
      if (targetIndex === this.activeIndex) return;
      this.playCard(this.cards[targetIndex], 'navigation');
    }

    handleKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.move(1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.playCard(this.cards[0], 'navigation');
      } else if (event.key === 'End') {
        event.preventDefault();
        this.playCard(this.cards[this.cards.length - 1], 'navigation');
      }
    }

    handleControl(event) {
      const audioButton = event.target.closest('[data-bf-ugc-audio]');
      if (audioButton && this.contains(audioButton)) {
        const audioCard = audioButton.closest('[data-bf-ugc-card]');
        if (audioCard) this.toggleSound(audioCard);
        return;
      }

      const button = event.target.closest('[data-bf-ugc-toggle]');
      if (!button || !this.contains(button)) return;

      const card = button.closest('[data-bf-ugc-card]');
      if (!card) return;

      if (!card.classList.contains('is-playing')) this.playCard(card, 'manual');
    }

    async playCard(card, origin = 'manual') {
      const index = this.cards.indexOf(card);
      if (index < 0) return;
      if (origin === 'automatic' && this.reducedMotion.matches) return;

      this.pauseAll(card);
      window.dispatchEvent(new CustomEvent('bf:media-play', {detail: {owner: this}}));
      this.activeIndex = index;
      this.programmaticScrollUntil = performance.now() + 250;
      this.scrollCardIntoView(card, 'auto');

      const video = card.querySelector('[data-bf-ugc-video]');
      const iframe = card.querySelector('[data-bf-ugc-iframe]');

      if (video) {
        this.hydrateVideo(video);
        if (origin !== 'manual') this.setCardMuted(card, true);
        try {
          await video.play();
          if (origin === 'manual') card.querySelector('[data-bf-ugc-audio]')?.focus({preventScroll: true});
        } catch (error) {
          this.markPlaying(card, false);
        }
      } else if (iframe) {
        this.hydrateIframe(iframe);
        this.playIframe(iframe);
        this.markPlaying(card, true);
        if (origin === 'manual') card.querySelector('[data-bf-ugc-audio]')?.focus({preventScroll: true});
      }

      this.update();
    }

    activateNearestCard() {
      this.scrollEndTimer = null;
      if (!this.itemOffset) return;
      if (performance.now() < this.programmaticScrollUntil) return;
      const targetIndex = Math.min(
        this.cards.length - 1,
        Math.max(0, Math.round(this.track.scrollLeft / this.itemOffset))
      );
      const targetCard = this.cards[targetIndex];
      if (!targetCard || (targetIndex === this.activeIndex && targetCard.classList.contains('is-playing'))) return;
      this.playCard(targetCard, 'navigation');
    }

    pauseAll(exceptCard = null) {
      this.cards.forEach((card) => {
        if (card !== exceptCard) this.pauseCard(card);
      });
    }

    pauseCard(card) {
      const video = card.querySelector('[data-bf-ugc-video]');
      const iframe = card.querySelector('[data-bf-ugc-iframe]');
      if (video && !video.paused) video.pause();
      if (iframe?.src) this.pauseIframe(iframe);
      this.setCardMuted(card, true);
      this.markPlaying(card, false);
    }

    async toggleSound(card) {
      if (!card.classList.contains('is-playing')) await this.playCard(card, 'manual');
      this.setCardMuted(card, !this.isCardMuted(card));
    }

    isCardMuted(card) {
      const video = card.querySelector('[data-bf-ugc-video]');
      if (video) return video.muted;
      return card.dataset.bfSound !== 'on';
    }

    setCardMuted(card, muted) {
      const video = card.querySelector('[data-bf-ugc-video]');
      if (video) video.muted = muted;
      card.dataset.bfSound = muted ? 'off' : 'on';
      this.applyIframeSound(card);

      const button = card.querySelector('[data-bf-ugc-audio]');
      if (!button) return;
      const label = muted ? button.dataset.soundOnLabel : button.dataset.soundOffLabel;
      button.setAttribute('aria-pressed', String(!muted));
      button.setAttribute('aria-label', label);
      const hiddenLabel = button.querySelector('[data-bf-ugc-audio-label]');
      if (hiddenLabel) hiddenLabel.textContent = label;
    }

    applyIframeSound(card) {
      const iframe = card.querySelector('[data-bf-ugc-iframe]');
      if (!iframe?.src) return;
      const muted = card.dataset.bfSound !== 'on';
      const message = iframe.dataset.provider === 'youtube'
        ? JSON.stringify({event: 'command', func: muted ? 'mute' : 'unMute', args: []})
        : JSON.stringify({method: 'setVolume', value: muted ? 0 : 1});
      iframe.contentWindow?.postMessage(message, '*');
    }

    markPlaying(card, isPlaying) {
      card.classList.toggle('is-playing', isPlaying);
      const button = card.querySelector('[data-bf-ugc-toggle]');
      if (!button) return;
      button.setAttribute('aria-pressed', String(isPlaying));
      button.setAttribute('aria-label', button.dataset.playLabel);
      const label = button.querySelector('[data-bf-ugc-toggle-label]');
      if (label) label.textContent = button.dataset.playLabel;
    }

    hydrateVideo(video) {
      if (video.dataset.loaded === 'true') return;
      video.querySelectorAll('source[data-src]').forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.dataset.loaded = 'true';
      video.load();
    }

    hydrateIframe(iframe) {
      if (iframe.src || !iframe.dataset.src) return;
      iframe.src = iframe.dataset.src;
    }

    playIframe(iframe) {
      const message = iframe.dataset.provider === 'youtube'
        ? JSON.stringify({event: 'command', func: 'playVideo', args: []})
        : JSON.stringify({method: 'play'});
      iframe.contentWindow?.postMessage(message, '*');
    }

    pauseIframe(iframe) {
      const message = iframe.dataset.provider === 'youtube'
        ? JSON.stringify({event: 'command', func: 'pauseVideo', args: []})
        : JSON.stringify({method: 'pause'});
      iframe.contentWindow?.postMessage(message, '*');
    }

    scrollCardIntoView(card, behavior = 'smooth') {
      const left = Math.min(card.offsetLeft, this.track.scrollWidth - this.track.clientWidth);
      this.track.scrollTo({
        left,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : behavior,
      });
    }

    isMostlyVisible(card) {
      const trackRect = this.track.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const visibleWidth = Math.max(0, Math.min(trackRect.right, cardRect.right) - Math.max(trackRect.left, cardRect.left));
      return visibleWidth >= cardRect.width * 0.6;
    }

    handleBlockSelect(event) {
      const card = event.target?.closest?.('[data-bf-ugc-card]');
      if (!card || !this.contains(card)) return;
      this.playCard(card, 'navigation');
    }
  }

  customElements.define('bf-product-media-carousel', BFProductMediaCarousel);
})();
